// Join-key helpers for aligning compareSOI response rows with the portfolio
// display rows in OutcomePanel.
//
// compareSOI re-keys each response row's `fund` to the SOI-side canonical
// label — the extracted header, which carries the legal-entity suffix
// ("MF Select II, L.P."). The portfolio display rows carry the transform's
// `legalEntity`, which does not ("MF Select II"). A raw string compare misses
// on every row and blanks the SOI column even though the backend matched at
// 100% coverage. Keying both sides through `normalizeFundLabel` — which mirrors
// the backend argus normalizer (`normalizeFundName` + `stripTrailingLegalSuffix`
// in compare-soi.service) — makes the suffix variance a non-issue.

/**
 * Normalize a fund label for equality matching: lowercase, strip punctuation
 * and collapse whitespace (mirrors backend `normalizeFundName`), then drop a
 * single trailing legal-entity token (mirrors `stripTrailingLegalSuffix`).
 * Unicode dash variants strip with ASCII ones — extractions write the same
 * fund with an en-dash where the fund table has a hyphen.
 *
 * `normalizeFundName` alone is not enough — it keeps the "lp" token, so
 * "mf select ii lp" still !== "mf select ii". The suffix strip only removes a
 * trailing token, never a prefix, so "MF Select" cannot collapse into
 * "MF Select II".
 */
export function normalizeFundLabel(fund: string): string {
  const normalized = fund
    .toLowerCase()
    .replace(/[.,:\-()[\]\u2010-\u2015\u2212]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return normalized.replace(/\s+(lp|llc|ltd|inc)$/, '');
}

/**
 * Stable key for joining a (company, fund) pair across the compareSOI response
 * and the portfolio display rows. Company names come from the same source on
 * both sides (dealName), so a trim + lowercase is sufficient there; the fund
 * side goes through `normalizeFundLabel` to absorb legal-suffix variance.
 */
export function compareSoiRowKey(companyName: string, fund: string): string {
  return `${companyName.trim().toLowerCase()}|${normalizeFundLabel(fund)}`;
}
